import { Controller } from "@hotwired/stimulus"

// Board mention autocomplete controller
// Detects @ in post/comment textareas and shows member suggestions
export default class extends Controller {
  static targets = ["input", "dropdown", "list"]

  connect() {
    this.selectedIndex = -1
    this.isOpen = false
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  handleInput(event) {
    const input = event.target
    const cursorPosition = input.selectionStart
    const text = input.value.substring(0, cursorPosition)

    const mentionMatch = text.match(/@([가-힣a-zA-Z0-9_]*)$/)

    if (mentionMatch) {
      const query = mentionMatch[1]
      if (query.length >= 1) {
        this.debouncedSearch(query)
      } else {
        this.hideSuggestions()
      }
    } else {
      this.hideSuggestions()
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        event.stopPropagation()
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        event.stopPropagation()
        this.moveSelection(-1)
        break
      case "Enter":
      case "Tab":
        if (this.selectedIndex >= 0) {
          event.preventDefault()
          event.stopPropagation()
          this.selectCurrentItem()
        }
        break
      case "Escape":
        event.preventDefault()
        this.hideSuggestions()
        break
    }
  }

  debouncedSearch(query) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.searchMembers(query), 200)
  }

  async searchMembers(query) {
    try {
      const response = await fetch(`/api/v1/members/search?q=${encodeURIComponent(query)}`)
      if (!response.ok) return

      const members = await response.json()
      if (members.length === 0) {
        this.hideSuggestions()
        return
      }

      this.renderSuggestions(members.slice(0, 8))
      this.isOpen = true
      this.selectedIndex = 0
      this.updateSelection()
      this.dropdownTarget.classList.remove("hidden")
    } catch (error) {
      console.error("Member search failed:", error)
    }
  }

  hideSuggestions() {
    this.isOpen = false
    this.selectedIndex = -1
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  renderSuggestions(members) {
    this.listTarget.innerHTML = members.map((member, index) => `
      <li class="px-3 py-2 cursor-pointer hover:bg-red-50 flex items-center ${index === this.selectedIndex ? 'bg-red-100' : ''}"
          data-action="click->board-mention#selectItem"
          data-nickname="${this.escapeHtml(member.mb_nick)}"
          data-mb-id="${this.escapeHtml(member.mb_id)}">
        <div class="h-7 w-7 rounded-full bg-red-500 flex items-center justify-center mr-2 flex-shrink-0">
          <span class="text-white text-xs font-medium">${this.escapeHtml((member.mb_nick || member.mb_id).charAt(0))}</span>
        </div>
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900 text-sm">${this.escapeHtml(member.mb_nick)}</span>
          <span class="text-gray-400 text-xs ml-1">@${this.escapeHtml(member.mb_id)}</span>
        </div>
      </li>
    `).join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  moveSelection(direction) {
    const items = this.listTarget.querySelectorAll("li")
    this.selectedIndex = Math.max(0, Math.min(items.length - 1, this.selectedIndex + direction))
    this.updateSelection()
  }

  updateSelection() {
    const items = this.listTarget.querySelectorAll("li")
    items.forEach((item, index) => {
      item.classList.toggle("bg-red-100", index === this.selectedIndex)
    })
  }

  selectCurrentItem() {
    const items = this.listTarget.querySelectorAll("li")
    const selected = items[this.selectedIndex]
    if (selected) {
      this.insertMention(selected.dataset.nickname)
    }
  }

  selectItem(event) {
    const nickname = event.currentTarget.dataset.nickname
    this.insertMention(nickname)
  }

  insertMention(nickname) {
    const input = this.inputTarget
    const cursorPosition = input.selectionStart
    const text = input.value

    const beforeCursor = text.substring(0, cursorPosition)
    const atPosition = beforeCursor.lastIndexOf("@")

    if (atPosition >= 0) {
      const before = text.substring(0, atPosition)
      const after = text.substring(cursorPosition)
      input.value = `${before}@${nickname} ${after}`
      const newPosition = atPosition + nickname.length + 2
      input.selectionStart = input.selectionEnd = newPosition
    }

    this.hideSuggestions()
    input.focus()
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
